import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './authprovider';

interface ProtectedRouteProps {
  redirectPath?: string;
  forbiddenRedirectPath?: string;
  requiredRole?: string;
}

/**
 * A component that protects routes by checking if the user is logged in.
 * If not logged in, it redirects to the specified path (defaults to /signin).
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  redirectPath = '/signin',
  forbiddenRedirectPath = '/account',
  requiredRole
}) => {
  const { isLoggedIn, checkIfLoggedIn, userRole } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(isLoggedIn);

  useEffect(() => {
    const verifyAuthentication = async () => {
      if (!isLoggedIn && !isChecking) {
        setIsChecking(true);
        // Double-check authentication status
        const authenticated = await checkIfLoggedIn();
        setIsAuthenticated(authenticated);
        setIsChecking(false);
      } else {
        setIsAuthenticated(isLoggedIn);
      }
    };

    verifyAuthentication();
  }, [isLoggedIn, checkIfLoggedIn, isChecking]);

  if (isChecking) {
    // You could return a loading indicator here if desired
    return null;
  }

  if (!isAuthenticated) {
    // Save the attempted URL for redirecting back after login
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  if (requiredRole && userRole !== requiredRole) {
    return <Navigate to={forbiddenRedirectPath} replace />;
  }

  // If authenticated, render the child routes
  return <Outlet />;
};
